import { useState, useLayoutEffect } from 'react';
import { useTranslation } from "react-i18next";
import { s } from "@/app/utils/s";
import {AuthScreenStatus, setState} from "@/app/features/auth";
import {useAppDispatch} from "@/app/hooks";

const useCreatePin = () => {
  const [code, setCode] = useState('');
  const [pinStatus, setPinStatus] = useState<CreatePinStatus>(CreatePinStatus.FIRST_STEP);
  const [codeRepeat, setCodeRepeat] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const updateRelativeSize = () => {
    const absoluteElement = document.getElementById('transitionItem');
    const relativeElement = document.getElementById('transitionItems');
    const rect = absoluteElement?.getBoundingClientRect();
    if (rect && relativeElement) {
      relativeElement.style.width = `${rect.width}px`;
      relativeElement.style.height = `${rect.height}px`;
    }
  };

  useLayoutEffect(() => {
    updateRelativeSize();
    window.addEventListener('resize', updateRelativeSize);
    return () => {
      window.removeEventListener('resize', updateRelativeSize);
    };
  }, []);

  const updateCode = async (code: string) => {
    setDisabled(!(code.length === 4));
    setCode(code);
  };

  const updateCodeRepeat = async (code: string) => {
    setDisabled(!(code.length === 4));
    setCodeRepeat(code);
  };

  const continued = async () => {
    const isFirstStep = pinStatus === CreatePinStatus.FIRST_STEP;

    if (error != null) {
      setError(null);
      setCode('');
      setCodeRepeat('');
      setPinStatus(CreatePinStatus.FIRST_STEP);
      setDisabled(true);
      return;
    }

    if (code.length === 4 && isFirstStep) {
      setDisabled(true);
      setPinStatus(CreatePinStatus.SECOND_STEP);
    } else if (codeRepeat.length === 4 && !isFirstStep && code === codeRepeat) {
      setLoading(true);
      dispatch(setState(AuthScreenStatus.WHO_ARE_YOU));
    } else if (codeRepeat.length === 4 && !isFirstStep && code !== codeRepeat) {
      setError(t(s.pinsDoNotMatch));
      return;
    } else {
      setLoading(false);
    }
    setError(null);
    return;
  };

  return {
    code,
    codeRepeat,
    setCode: updateCode,
    setCodeRepeat: updateCodeRepeat,
    pinStatus,
    continued,
    loading,
    error,
    disabled,
  };
};

export const CreatePinStatus = {
  FIRST_STEP: 'FIRST_STEP',
  SECOND_STEP: 'SECOND_STEP',
} as const;

type CreatePinStatus = typeof CreatePinStatus[keyof typeof CreatePinStatus];

export default useCreatePin;